import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { mapBedbankBoardCode } from 'api/mappers/bedbankRateMap'
import { getOfferInclusionDescriptionFromMarkdown } from 'api/mappers/offerInclusionMap'
import { EmptyArray } from 'lib/array/arrayUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

type ServerUnifiedRate = definitions['UnifiedRate']
type ServerMetaKey = ServerUnifiedRate['meta']
type ServerLPCMetaKey = definitions['LPCMetaKey']
type ServerLPPMetaKey = definitions['LPPMetaKey']
type ServerRateType = ServerMetaKey['type']
type ServerCancellationPolicy = definitions['CancellationPolicies']
type ServerInclusion = definitions['Inclusion']
type ServerRefundableStatus = definitions['RefundableStatus']

function isLpcMeta(meta: ServerMetaKey): meta is ServerLPCMetaKey {
  return meta.type === 'reservation'
}

function isBedbankMeta(meta: ServerMetaKey): meta is ServerLPPMetaKey {
  return meta.type === 'bedbank'
}

function mapInclusionDescription(
  serverRateType: ServerRateType,
  text: string,
): string {
  switch (serverRateType) {
    case 'reservation':
      return getOfferInclusionDescriptionFromMarkdown(text)
    case 'bedbank':
      return text
    default:
      assertUnreachable(serverRateType)
  }
}

function mapInclusion(
  serverRateType: ServerRateType,
  { text, icon }: ServerInclusion,
  inclusionType: 'normal' | 'bonus' | 'luxPlus',
): App.OfferInclusion {
  const description = mapInclusionDescription(serverRateType, text)
  return {
    id: uuidv4(),
    description,
    symbol: determineOfferFeatureSymbol(icon || description),
    isBonus: inclusionType === 'bonus',
    isLuxPlusBonus: inclusionType === 'luxPlus',
  }
}

function mapCancellationPolicy({
  amount,
  description,
  start,
  end,
  nonRefundable,
}: ServerCancellationPolicy): App.Property.CancellationPolicy {
  return {
    startIsoDateTime: start,
    endIsoDateTime: end,
    penaltyPrice: amount,
    isFullyNonRefundable: nonRefundable,
    originalDescription: description,
  }
}

function mapHeadlineCancellationPolicy(
  serverRefundableStatus: ServerRefundableStatus,
  serverFullyRefundableUntil: string | undefined,
  serverPartiallyRefundableUntil: string | undefined,
): ['fullyRefundable' | 'partiallyRefundable', string] | ['nonRefundable', undefined] {
  switch (serverRefundableStatus) {
    case 'refundable':
      return ['fullyRefundable', serverFullyRefundableUntil!]
    case 'partially':
      return ['partiallyRefundable', serverPartiallyRefundableUntil!]
    case 'non-refundable':
      return ['nonRefundable', undefined]
    default:
      return assertUnreachable(serverRefundableStatus)
  }
}

export default function propertyRateMap(serverRate: ServerUnifiedRate): App.PropertyRate {
  const serverRateMeta = serverRate.meta
  // NB: These fields were last audited against v0.5.4 of `contract-svc-accommodation`.
  const fixedTotal = {
    ...serverRate.total,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: serverRate.total.displayPrice - serverRate.total.propertyFees,
  }
  const fixedLuxPlus = serverRate.luxPlus && {
    ...serverRate.luxPlus,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: serverRate.luxPlus.displayPrice - serverRate.luxPlus.propertyFees,
  }
  const [headlineCancellationPolicyType, headlineCancellationPolicyStart] = mapHeadlineCancellationPolicy(serverRate.refundableStatus, serverRate.fullRefundableUntil, serverRate.partiallyRefundableUntil)
  const baseRate: Omit<App.BasePropertyRate, 'exceedsIncludedGuests' | 'isMobilePromotion' | 'hasPaymentSchedules' | 'name'> = {
    /* Identifiers */
    propertyId: serverRate.propertyId,
    roomId: serverRate.roomId,
    rateId: serverRate.id,
    /* Deduplication keys */
    groupKey: serverRate.groupKey,
    bundlingKey: serverRate.bundlingKey,
    isFlightBundle: !!serverRateMeta.isFlightBundle,
    /* Inclusions */
    inclusions: serverRate.inclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')),
    bonusInclusions: serverRate.bonusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'bonus')),
    luxPlusInclusions: serverRate.luxPlusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'luxPlus')),
    /* Cancellation policies */
    cancellationPolicies: serverRate.cancellationPolicies.map(mapCancellationPolicy),
    /* Pricing */
    /* (Irrelevant) */
    // `serverRate.occupancies` is not set.
    // `serverRate.margin` is filtered out for non-admins.
    // `serverRate.groupScore` is filtered out for non-admins.
    /* TODO: Audit, document, tighten contract */
    total: fixedTotal,
    luxPlusTotal: fixedLuxPlus,
    fullyRefundableUntil: serverRate.fullRefundableUntil,
    headlineCancellationPolicyType,
    headlineCancellationPolicyStart,
    includesFlights: false,
    includedGuestsExceededPolicy: undefined,
  }
  if (isLpcMeta(serverRateMeta)) {
    // TODO: Remove hack once accommodation service returns this value for us.
    fixedTotal.discount = getValueOffPercent(fixedTotal.valuedUpToPrice, fixedTotal.displayPrice)
    if (fixedLuxPlus) {
      fixedLuxPlus.discount = getValueOffPercent(fixedLuxPlus.valuedUpToPrice, fixedLuxPlus.displayPrice)
    }
    return {
      ...baseRate,
      /* Identifiers */
      type: 'lpc',
      offerId: serverRateMeta.offerId,
      lpcPropertyId: serverRateMeta.reservationPropertyId,
      packageId: serverRateMeta.packageId ?? serverRateMeta.lePackageId,
      lpcRoomId: serverRateMeta.reservationRoomTypeId,
      lpcRateId: serverRateMeta.reservationRoomRateId,
      /* Status flags */
      exceedsIncludedGuests: !!serverRateMeta.exceedsIncludedGuests,
      isMobilePromotion: false,
      /* Configuration options */
      hasPaymentSchedules: false,
      /* Content */
      name: serverRateMeta.name,
    }
  } else if (isBedbankMeta(serverRateMeta)) {
    // TODO: Remove hack once accommodation service returns this value for us.
    fixedTotal.discount = Math.floor(fixedTotal.discount * 100)
    if (fixedLuxPlus) {
      fixedLuxPlus.discount = Math.floor(fixedLuxPlus.discount * 100)
    }
    return {
      ...baseRate,
      /* Identifiers */
      type: 'bedbank',
      bedbankSupplier: serverRateMeta.supplier,
      bedbankSupplierCode: serverRateMeta.supplierCode!, // This _should_ be always set.
      bedbankPropertyId: serverRateMeta.bedbankPropertyId,
      bedbankRoomId: serverRateMeta.bedbankRoomId,
      bedbankRateId: serverRateMeta.bedbankRateId,
      /* Deduplication keys */
      rateGroupId: serverRateMeta.rateGroupId,
      boardCode: mapBedbankBoardCode(serverRate.boardCode),
      /* Status flags */
      exceedsIncludedGuests: false,
      isMobilePromotion: !!serverRateMeta.isMobilePromotion,
      pricingMethod: serverRateMeta.pricingMethod,
      /* Configuration options */
      hasPaymentSchedules: !!serverRateMeta.paymentSchedule,
      bedGroups: serverRateMeta.bedGroups ?? EmptyArray,
      /* Content */
      name: undefined,
    }
  }
  assertUnreachable(serverRateMeta)
}
